import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import { BaseRedoInput } from "@redotech/redo-web/arbiter-components/input/base-redo-text-input";
import { RedoList } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useRef, useState } from "react";
import { useAssigneeSelector } from "../hooks/use-assignee-selector";

export interface AssigneeSelectorDropdownContentProps {
  supportTicketId: SupportTicketId;
  mongoAssigneeId: MongoUserId | null;
}

export const AssigneeSelectorDropdownContent = memo(
  function AssigneeSelectorDropdownContent({
    supportTicketId,
    mongoAssigneeId,
  }: AssigneeSelectorDropdownContentProps) {
    const {
      assigneeListItems,
      selectedItem,
      canEditAssignee,
      handleItemSelected,
      searchText,
      setSearchText,
    } = useAssigneeSelector({ supportTicketId, mongoAssigneeId });

    const [focusedIndex, setFocusedIndex] = useState<number | undefined>(
      undefined,
    );
    const containerRef = useRef<HTMLDivElement>(null);

    if (!canEditAssignee) {
      return (
        <Flex align="center" dir="column" gap="sm" p="lg">
          <Text fontSize="xs" textColor="tertiary">
            You don't have permission to assign tickets
          </Text>
        </Flex>
      );
    }

    return (
      <Flex dir="column" gap="none" ref={containerRef}>
        <Flex
          borderBottomWidth="1px"
          borderColor="primary"
          borderStyle="solid"
          px="lg"
          py="md"
        >
          <BaseRedoInput
            autoFocus
            placeholder="Search users..."
            setValue={setSearchText}
            size="sm"
            value={searchText}
          />
        </Flex>
        <Flex px="lg" py="md">
          {assigneeListItems.length === 0 ? (
            <Text fontSize="xs" textColor="tertiary">
              {searchText ? "No matching users" : "No users available"}
            </Text>
          ) : (
            <RedoList
              focusedIndex={focusedIndex}
              items={assigneeListItems}
              itemSelected={({ item }) => handleItemSelected(item.value)}
              refToListenTo={containerRef.current}
              selectedItems={selectedItem?.id}
              setFocusedIndex={setFocusedIndex}
              size="sm"
            />
          )}
        </Flex>
      </Flex>
    );
  },
);
